import * as React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as recipientAccountsActions from '../../../redux/actions/RecipientAccountsActions';
import { getDuplicateRecipientsDetails, resolveDuplicateRecipients } from '../../../redux/actions/DuplicateRecipientsActions';
import { toggleSidebar, resetFilters } from '../../../redux/actions/SidebarFilterActions';
import { PermissionTypes } from '../../../lib/systemVariables';
import { TableNames } from '../../../lib/constants';
import { getQueryParams, setQueryParams } from '../../../lib/urlUtils';
import { exportCSVFile } from '../../../lib/utils';
import { debounce } from 'throttle-debounce';

import NavigationComponent from '../../commonComponents/util/NavigationComponent';
import LoadingSpinner from '../../commonComponents/util/LoadingSpinner';
import FadeOutErrorMessage from '../../commonComponents/util/FadeOutErrorMessage';
import SidebarFilter from '../../commonComponents/shared/SidebarFilter';
import RecipientAccountsTable from './RecipientAccountsTable';
import MergeDuplicatesModal from '../manageDuplicates/MergeDuplicatesModal';
import RecipientReportModal from './RecipientReportModal';
import PledgeReportModal from './PledgeReportModal';

class RecipientAccountsPage extends NavigationComponent {
    constructor(props) {
        super(props);

        this.state = {
            showModals: {
                search: '',
                mergeDuplicatesModal: false,
                reportModal: false,
                pledgeReportModal: false,
            }
        };

        this.filterRecipients = this.filterRecipients.bind(this);
    }

    componentDidMount() {
        if (getQueryParams().applyFilters) {
            this.setState({search: this.props.filters.elasticSearchText || ''}, () => {
                this.props.actions.getRecipientAccounts(this.state.search, this.props.recipientsTable.page, this.props.recipientsTable.resultsView, this.props.recipientsTable.sort);
            });
        } else { //manually reset and reload data, only if not popped from stack
            this.props.actions.resetFilters(TableNames.Recipients, true); //reset and apply filters
            this.props.actions.filterRecipients(); //reset table and reload recipients
        }
    }

    componentWillUnmount() {
        if (this.props.recipientsTable.selected.length) {
            this.props.actions.clearRecipientsSelection();
        }
    }

    getRecipientAccounts = (...props) => {
        this.props.actions.getRecipientAccounts(this.state.search, ...props);
        if (props && !getQueryParams().applyFilters) {
            setQueryParams({ applyFilters: true });
        }
    }

    getRecipientsForExport = async (props) => {
        await this.props.actions.getRecipientsForExport(props.fields, props.groupBy);
    }

    downloadPledgeReport = async (params) => {
        const response = await this.props.actions.getPledgesReport(
            params.fundIds,
            params.includeClosed
        );

        if (!response || !response.success) {
            throw new Error('error');
        }

        const data = response.data || {};
        const values = data.values || data.Values || data;
        const records = Array.isArray(values) ? values : [];

        if (!records.length) {
            throw new Error('noData');
        }

        const exportHeaders = Object.keys(records[0]).map(key => ({
            label: key,
            value: key,
            default: ''
        }));

        const dateNow = `${new Date().toLocaleDateString().replace(/\//g, "_")}`;
        const reportName = `pledges_${dateNow}`;

        exportCSVFile(exportHeaders, records, reportName);
    }
    
    search = e => {
        this.setState({search: e.target.value});
        if (!getQueryParams().applyFilters) {
            setQueryParams({ applyFilters: true });
        }
        this.searchDebounce();
    }

    searchDebounce = debounce(400, () => {
        this.props.actions.getRecipientAccounts(this.state.search, this.props.recipientsTable.page, this.props.recipientsTable.resultsView)
    });

    getSortBy(updatedFilters) {
        let tableSort = this.props.recipientsTable.sort;

        if (updatedFilters.recipientSearch) {
            tableSort = { by: "recipientSearchScore", direction: "Descending" }
        } else if (updatedFilters.lastName) {
            tableSort = { by: "lastNameDiff", direction: "Ascending" }
        } else if (updatedFilters.firstName) {
            tableSort = { by: "firstNameDiff", direction: "Ascending" }
        } else if (this.props.recipientsTable.sort.by === "recipientSearchScore" ||
                   this.props.recipientsTable.sort.by === "lastNameDiff" ||
                   this.props.recipientsTable.sort.by === "firstNameDiff") {
            tableSort = undefined;
        }
        
        return tableSort;
    }

    filterRecipients(updatedFilters) {
        const tableSort = this.getSortBy(updatedFilters);
        
        this.props.actions.filterRecipients(updatedFilters, this.state.search, this.props.recipientsTable.resultsView, tableSort);

        if (updatedFilters && !getQueryParams().applyFilters) {
            setQueryParams({ applyFilters: true });
        }
    }

    getDuplicatesHeader = () => {
        const { duplicateRecipients, recipientsTable } = this.props;
        const selection = recipientsTable.selected;

        return <div className="text-right subheader-btns flex-center duplicates-container">
            <p>Duplicate Recipients:
                <span>{selection[0].name}  <i className="material-icons" onClick={() => this.props.actions.toggleSelectRecipient(selection[0])}>close</i></span>
                <span>{"  &  "}</span>
                {selection[1]
                    ? <span>{selection[1].name}  <i className="material-icons" onClick={() => this.props.actions.toggleSelectRecipient(selection[1])}>close</i></span>
                    : <span className="accent-text">None Selected</span>}
            </p>
            {duplicateRecipients.resolvingDuplicates.loading ? <LoadingSpinner height="12" />
                : <button className="btn btn-accent btn-small" disabled={selection.length !== 2} onClick={this.mergeDuplicates}>Merge</button>}
            <FadeOutErrorMessage show={!duplicateRecipients.resolvingDuplicates.success} message={duplicateRecipients.resolvingDuplicates.message} />
        </div>;
    }

    mergeDuplicates = () => {
        const duplicates = this.props.recipientsTable.selected;
        this.props.actions.getDuplicateRecipientsDetails(duplicates[0].applicantId, duplicates[1].applicantId);

        //open modal and clear out table selection
        this.toggleModal("mergeDuplicatesModal");
        duplicates.forEach(recipient => this.props.actions.toggleSelectRecipient(recipient));
    }

    render() {
        return (
            <div className={`page page-with-sidebar applications ${!this.props.sidebarVisible ? "page-with-sidebar-hidden" : ""}`}>
                <SidebarFilter type={TableNames.Recipients} search={this.filterRecipients} />
                    <div className="subheader recipients-subheader">
                        <div className="flex-justify flex-center">
                            <div className="flex-one">
                                <i className="material-icons sidebar-slide-out" onClick={() => this.props.actions.toggleSidebar(TableNames.Recipients)}>filter_list</i>
                                <h2 className="page-header">Recipients</h2>
                            </div>
                            <div className="flex-one">
                                <div className="elastic-search">
                                    <div className="search-input">
                                        <i className="material-icons">search</i>
                                        <input type="text" name="search" placeholder="Search (Max Results: 100)" value={this.state.search || ''} onChange={e => this.search(e)} />
                                    </div>
                                </div>
                            </div>
                            {this.props.userPermissions.manage && this.props.recipientsTable.selected.length > 0
                                && this.getDuplicatesHeader()}
                            {/* <div className="text-right subheader-btns flex-center">
                                <Link to="/recipient-accounts/duplicates" className="btn btn-duplicate btn-small">Find Duplicates</Link>
                            </div> */}
                        
                            <div className="flex-one">
                                
                                <div className="text-right subheader-btns">
                                    {this.props.userPermissions.report &&
                                         <div className="subheader-btns">
                                            <button className="btn btn-flat btn-small" onClick={() => this.toggleModal("reportModal")} disabled={!this.props.recipientsTable.data.records.length}>
                                                <img src={`/images/export-icon.png`}
                                                className="icon-left" alt="export-icon" />Export</button>
                                            <button className="btn btn-flat btn-small" onClick={() => this.toggleModal("pledgeReportModal")} disabled={!this.props.recipientsTable.data.records.length}>
                                                <img src={`/images/export-icon.png`}
                                                className="icon-left" alt="export-icon" />Pledge Report</button>
                                         </div>
                                    }
                                    <Link to="/recipient-accounts/references" className="btn btn-primary btn-small">Manage References</Link>
                                </div>
                            </div>
                        </div>
                    </div>

                <RecipientAccountsTable
                    {...this.props.recipientsTable}
                    updateTable={this.props.actions.updateTable}
                    getRecipients={this.getRecipientAccounts}
                    deleteRecipient={this.props.actions.deleteRecipientAccount}
                    canDelete={this.props.userPermissions.manage}
                    canViewApplications={this.props.userPermissions.viewApplications}
                    selectRecipient={this.props.actions.toggleSelectRecipient} />

                <MergeDuplicatesModal
                    show={this.state.showModals.mergeDuplicatesModal}
                    close={() => this.toggleModal("mergeDuplicatesModal")}
                    duplicatesDetails={this.props.duplicateRecipients.duplicatesDetails}
                    mergeDuplicates={this.props.actions.resolveDuplicateRecipients}
                    onMergeDuplicates={() => this.props.actions.getRecipientAccounts(this.state.search, this.props.recipientsTable.page, this.props.recipientsTable.resultsView, this.props.recipientsTable.sort)}
                    mergingDuplicates={this.props.duplicateRecipients.resolvingDuplicates} />

                <RecipientReportModal
                    getDataForExport={this.getRecipientsForExport.bind(this)}
                    export={this.props.export}
                    show={this.state.showModals.reportModal}
                    close={() => this.toggleModal("reportModal")}/>

                <PledgeReportModal
                    show={this.state.showModals.pledgeReportModal}
                    close={() => this.toggleModal("pledgeReportModal")}
                    onDownload={this.downloadPledgeReport}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        ...state.recipientAccounts,
        recipientsTable: state.recipientsTable,
        sidebarVisible: state.recipientsSidebarFilters.showSidebar,
        duplicateRecipients: state.duplicateRecipients,
        userPermissions: {
            manage: state.userProfile.permissions.data.indexOf(PermissionTypes.RecipientAccntsManage) >= 0,
            report: state.userProfile.permissions.data.indexOf(PermissionTypes.Reporting) >= 0,
            viewApplications: state.userProfile.permissions.data.indexOf(PermissionTypes.ApplicationsView) >= 0
        }
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({
            ...recipientAccountsActions,
            getDuplicateRecipientsDetails,
            resolveDuplicateRecipients,
            toggleSidebar,
            resetFilters
        }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(RecipientAccountsPage);


// WEBPACK FOOTER //
// ./src/components/recipientAccountsComponents/recipientAccounts/RecipientAccountsPage.jsx